import React from "react";
import { StyleSheet, View } from "@react-pdf/renderer";
import { get } from "lodash-es";
import { CONTRACT_INFO_STEP2_FIELD as FIELD } from "config/documentConstant";
import ProfitabilityField from "./ProfitabilityField";
import { ContractInfoValueText } from "./ContractInfoPdfLayout";

interface ContractInfoProfitabilityTableProps {
  documentData: Record<string, unknown>;
}

interface ProfitabilityRow {
  category?: string;
  amount?: number | string | null;
  note?: string | null;
}

interface ProfitabilityUnits {
  [key: string]: string;
}

const styles = StyleSheet.create({
  table: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },
  groupRow: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
    gap: "3mm",
    paddingTop: "0.8mm",
    paddingBottom: "0.8mm",
  },
  groupLabelWrap: {
    width: "13mm",
    display: "flex",
    alignItems: "flex-start",
    alignSelf: "stretch",
    justifyContent: "center",
  },
  groupLabel: {
    fontWeight: 700,
    lineHeight: 1.3,
  },
  groupContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  divider: {
    borderBottom: "0.2mm solid #C6C8CD",
  },
});

const getSharedUnit = (units: ProfitabilityUnits) => {
  return (
    units["0"] ||
    Object.values(units).find(
      (value): value is string => typeof value === "string" && value.length > 0,
    ) ||
    "1"
  );
};

const normalizeProfitabilityRows = (rows: ProfitabilityRow[]) => {
  return FIELD.PROFITABILITY.rows.map((defaultRow, index) => ({
    ...defaultRow,
    ...(rows[index] ?? {}),
    category: rows[index]?.category || defaultRow.category,
  }));
};

const ContractInfoProfitabilityTable = ({
  documentData,
}: ContractInfoProfitabilityTableProps) => {
  const profitabilityRows =
    (get(documentData, FIELD.PROFITABILITY.key) as
      | ProfitabilityRow[]
      | undefined) || [];
  const profitabilityUnits =
    (get(documentData, FIELD.PROFITABILITY_UNITS.key) as
      | ProfitabilityUnits
      | undefined) || {};

  const normalizedRows = normalizeProfitabilityRows(profitabilityRows);
  const sharedUnit = getSharedUnit(profitabilityUnits);
  const sales = normalizedRows[0];
  const costs = normalizedRows.slice(1, 5);
  const profits = normalizedRows.slice(5, 7);

  return (
    <View style={styles.table}>
      <View style={[styles.groupRow, styles.divider]}>
        <View style={styles.groupLabelWrap}>
          <ContractInfoValueText style={styles.groupLabel}>
            매출액
          </ContractInfoValueText>
        </View>
        <View style={styles.groupContent}>
          <ProfitabilityField field={sales} hideCategory unit={sharedUnit} />
        </View>
      </View>

      <View style={[styles.groupRow, styles.divider]}>
        <View style={styles.groupLabelWrap}>
          <ContractInfoValueText style={styles.groupLabel}>
            {"매출\n원가"}
          </ContractInfoValueText>
        </View>
        <View style={styles.groupContent}>
          {costs.map((field, index) => (
            <ProfitabilityField
              key={`${field?.category || "cost"}-${index}`}
              field={field}
              borderBottom={index < costs.length - 1}
              unit={sharedUnit}
            />
          ))}
        </View>
      </View>

      <View style={styles.groupRow}>
        <View style={styles.groupLabelWrap}>
          <ContractInfoValueText style={styles.groupLabel}>
            {"사업\n이익"}
          </ContractInfoValueText>
        </View>
        <View style={styles.groupContent}>
          {profits.map((field, index) => (
            <ProfitabilityField
              key={`${field?.category || "profit"}-${index}`}
              field={field}
              borderBottom={index < profits.length - 1}
              unit={sharedUnit}
            />
          ))}
        </View>
      </View>
    </View>
  );
};

export default ContractInfoProfitabilityTable;
